import * as React from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '../utils'

export interface SelectItem {
  id: string
  name: string
}

export interface SelectProps {
  label?: string
  items: SelectItem[]
  selectedKey?: string
  onSelectionChange?: (key: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  id?: string
  name?: string
}

export function Select({
  label,
  items,
  selectedKey,
  onSelectionChange,
  placeholder = 'Select...',
  className,
  disabled,
  id,
  name,
}: SelectProps) {
  const selectId = id ?? React.useId()
  
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={selectId} className="text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={selectId}
          name={name}
          value={selectedKey ?? ''}
          onChange={(e) => onSelectionChange?.(e.target.value)}
          disabled={disabled}
          className={cn(
            'w-full px-3 py-2 pr-10',
            'appearance-none',
            'bg-white border border-gray-300 rounded-lg',
            'text-gray-900',
            'hover:border-gray-400',
            'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
            'transition-all duration-150 ease-out',
            !selectedKey && 'text-gray-400'
          )}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

// For API compatibility - not used with native select
export interface SelectItemProps {
  children: React.ReactNode
  className?: string
}

export function SelectItem({ children }: SelectItemProps) {
  return <>{children}</>
}
